import {
  CUSTOMER_TYPES,
  INVOICE_PAY_TO_TYPES,
  LEAD_TYPES,
  POLICY_CREATED_TYPE,
  POLICY_MEMBER_LIST_GENDER,
  POLICY_MEMBER_LIST_MARITAL_STATUS,
  POLICY_MEMBER_LIST_RELATION_TYPE,
} from '@shared/constants';

export const bmsGetCustomerTypeFromLeadType = (
  type: LEAD_TYPES
): CUSTOMER_TYPES | null => {
  switch (type) {
    case LEAD_TYPES.COMPANY_GROUP:
      return CUSTOMER_TYPES.COMPANY_GROUP;

    case LEAD_TYPES.INDIVIDUAL:
      return CUSTOMER_TYPES.INDIVIDUAL;

    default:
      return null;
  }
};

export const bmsGetCustomerCreatedMode = (
  type: POLICY_CREATED_TYPE
): string => {
  switch (type) {
    case POLICY_CREATED_TYPE.AUTO:
      return 'Flow';

    case POLICY_CREATED_TYPE.MANUAL:
      return 'Direct';

    case POLICY_CREATED_TYPE.BULK:
      return 'Bulk Flow';

    default:
      return '';
  }
};

export const bmsGetEmployeeRelationshipList = (
  translate: any
): {
  key: POLICY_MEMBER_LIST_RELATION_TYPE;
  value: string;
}[] => {
  return Object.entries(POLICY_MEMBER_LIST_RELATION_TYPE).map(([, value]) => {
    return {
      key: value,
      value: translate.instant(bmsGetEmployeeRelationshipName(value)),
    };
  });
};

export const RELATIONSHIP_NAME_MAP: Record<
  POLICY_MEMBER_LIST_RELATION_TYPE,
  string
> = {
  [POLICY_MEMBER_LIST_RELATION_TYPE.DEPENDANT]: 'Dependent',
  [POLICY_MEMBER_LIST_RELATION_TYPE.SELF]: 'Self',
  [POLICY_MEMBER_LIST_RELATION_TYPE.SPOUSE]: 'Spouse',
};

// Build reverse map once
const REVERSE_RELATIONSHIP_MAP: Record<
  string,
  POLICY_MEMBER_LIST_RELATION_TYPE
> = Object.entries(RELATIONSHIP_NAME_MAP).reduce((acc, [key, value]) => {
  acc[value.toLowerCase()] = key as POLICY_MEMBER_LIST_RELATION_TYPE;
  return acc;
}, {} as Record<string, POLICY_MEMBER_LIST_RELATION_TYPE>);

// 🔹 Get display name from enum
export const bmsGetEmployeeRelationshipName = (
  relationship: POLICY_MEMBER_LIST_RELATION_TYPE,
  isMapper = true
): string => {
  return RELATIONSHIP_NAME_MAP[relationship] ?? (isMapper ? '' : '-');
};

// 🔹 Get enum from display name
export const bmsGetEmployeeRelationshipValue = (
  name: string
): POLICY_MEMBER_LIST_RELATION_TYPE | undefined => {
  return REVERSE_RELATIONSHIP_MAP[name?.trim().toLowerCase()];
};

export const bmsGetEmployeeMaritalStatusList = (
  translate: any
): {
  key: POLICY_MEMBER_LIST_MARITAL_STATUS;
  value: string;
}[] => {
  return Object.entries(POLICY_MEMBER_LIST_MARITAL_STATUS).map(([, value]) => {
    return {
      key: value,
      value: translate.instant(bmsGetEmployeeMaritalStatusName(value)),
    };
  });
};

export const MARITAL_STATUS_NAME_MAP: Record<
  POLICY_MEMBER_LIST_MARITAL_STATUS,
  string
> = {
  [POLICY_MEMBER_LIST_MARITAL_STATUS.MARRIED]: 'Married',
  [POLICY_MEMBER_LIST_MARITAL_STATUS.UNMARRIED]: 'UnMarried',
};

// Reverse map built once for O(1) lookups
export const REVERSE_MARITAL_STATUS_MAP: Record<
  string,
  POLICY_MEMBER_LIST_MARITAL_STATUS
> = Object.entries(MARITAL_STATUS_NAME_MAP).reduce((acc, [key, value]) => {
  acc[value.toLowerCase()] = key as POLICY_MEMBER_LIST_MARITAL_STATUS;
  return acc;
}, {} as Record<string, POLICY_MEMBER_LIST_MARITAL_STATUS>);

// Get display name from enum
export const bmsGetEmployeeMaritalStatusName = (
  status: POLICY_MEMBER_LIST_MARITAL_STATUS,
  isMapper = true
): string => {
  return MARITAL_STATUS_NAME_MAP[status] ?? (isMapper ? '' : '-');
};

// Get enum value from display name (case-insensitive)
export const bmsGetEmployeeMaritalStatusValue = (
  name: string
): POLICY_MEMBER_LIST_MARITAL_STATUS | undefined => {
  return REVERSE_MARITAL_STATUS_MAP[name?.trim().toLowerCase()];
};

export const bmsGetEmployeeGenderList = (
  translate: any
): {
  key: POLICY_MEMBER_LIST_GENDER;
  value: string;
}[] => {
  return Object.entries(POLICY_MEMBER_LIST_GENDER).map(([, value]) => {
    return {
      key: value,
      value: translate.instant(bmsGetEmployeeGenderName(value)),
    };
  });
};

export const GENDER_NAME_MAP = {
  [POLICY_MEMBER_LIST_GENDER.MALE]: 'Male',
  [POLICY_MEMBER_LIST_GENDER.FEMALE]: 'Female',
};

export const bmsGetEmployeeGenderName = (
  relationship: string,
  isMapper = true
) => {
  const name = GENDER_NAME_MAP[relationship as keyof typeof GENDER_NAME_MAP];
  return name ?? (isMapper ? '' : '-');
};

export const bmsGetEmployeeGenderValue = (
  genderName: string
): POLICY_MEMBER_LIST_GENDER | undefined => {
  const entry = Object.entries(GENDER_NAME_MAP).find(
    ([, name]) => name.toLowerCase() === genderName?.trim().toLowerCase()
  );
  return entry?.[0] as POLICY_MEMBER_LIST_GENDER | undefined;
};

export const partialToFull = <T>(x: Partial<T>): T => {
  return x as T;
};

export const bmsGetPayToTypeValue = (type: INVOICE_PAY_TO_TYPES): string => {
  switch (type) {
    case INVOICE_PAY_TO_TYPES.BROKER:
      return 'Broker';
    case INVOICE_PAY_TO_TYPES.CUSTOMER:
      return 'Customer';

    case INVOICE_PAY_TO_TYPES.INSURER:
    case INVOICE_PAY_TO_TYPES.INSURER_PREMIUM_FORWARD:
      return 'Insurer';

    default:
      return '';
  }
};

export const viewDocument = (file: File) => {
  const viewableTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
  ];

  const url = URL.createObjectURL(file);

  if (viewableTypes.includes(file.type)) {
    window.open(url, '_blank');
    // Delay revoke to give browser time to open the file
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link); // Required for some browsers
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000); // Delay revoke to allow download
  }
};

export const viewDocumentFromUrl = (
  url: string,
  type: any,
  fileName: string
) => {
  const viewableTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
  ];

  const mimeType = type || '';

  if (viewableTypes.includes(mimeType)) {
    const newWindow = window.open(url, '_blank');
    if (
      !newWindow ||
      newWindow.closed ||
      typeof newWindow.closed === 'undefined'
    ) {
      // Fallback for blocked pop-up
      window.location.href = url;
    }
  } else {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export function addWorkingDays(startDate: Date, slaDays: number): string {
  const result = new Date(startDate);

  let addedDays = 0;
  while (addedDays < slaDays) {
    result.setDate(result.getDate() + 1);

    const day = result.getDay();
    if (day !== 0 && day !== 6) {
      addedDays++;
    }
  }

  // Format to dd/mm/yyyy
  const dd = String(result.getDate()).padStart(2, '0');
  const mm = String(result.getMonth() + 1).padStart(2, '0');
  const yyyy = result.getFullYear();

  return `${dd}/${mm}/${yyyy}`;
}
